import { useState } from 'react';
import { Ban, CircleCheck, Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PcmpRequestDecision } from '../../types/pcmpRequested.types';
import { PcmpRequestActDialog } from './PcmpRequestActDialog';

interface PcmpRequestActButtonsProps {
  proposalId: number;
  refNo: string;
  proposedAmount: number;
  /** The viewer may decide the open request. */
  canAct: boolean;
  /** The request is still Pending, so it can be marked Processing. */
  canProcess: boolean;
  /** Why approving would be refused now; the dialog then explains instead of approving. */
  approveBlockedReason: string | null;
  approveWarning?: string | null | undefined;
  /** The open request's category, for the warnings of a Proposal Correction. */
  categoryCode?: string | null | undefined;
  /** Process only: approve and reject happen on the proposal (the Proposal Correction tab). */
  processOnly?: boolean;
  className?: string;
}

/** Process, Approve and Reject for one proposal's open request, from the API's capabilities. */
export function PcmpRequestActButtons({
  proposalId,
  refNo,
  proposedAmount,
  canAct,
  canProcess,
  approveBlockedReason,
  approveWarning,
  categoryCode,
  processOnly = false,
  className,
}: PcmpRequestActButtonsProps) {
  const [decision, setDecision] = useState<PcmpRequestDecision | null>(null);

  // An open dialog outlives the capability: a 409 refresh clears canAct once the
  // request moved on, and its message must stay readable.
  if (!canAct && decision === null) return null;
  if (processOnly && !canProcess && decision === null) return null;

  return (
    <>
      {canAct ? (
        <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
          {canProcess ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setDecision('PROCESS')}
              className="gap-1.5"
            >
              <Hourglass className="size-3.5" />
              Process
            </Button>
          ) : null}
          {processOnly ? null : (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setDecision('APPROVE')}
                title={approveBlockedReason ?? undefined}
                className={cn('gap-1.5', approveBlockedReason && 'text-muted-foreground')}
              >
                <CircleCheck className="size-3.5" />
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setDecision('REJECT')}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <Ban className="size-3.5" />
                Reject
              </Button>
            </>
          )}
        </div>
      ) : null}
      {/* Mounted only while open, so it starts empty. */}
      {decision ? (
        <PcmpRequestActDialog
          target={{
            decision,
            count: 1,
            amount: proposedAmount,
            run: { kind: 'single', proposalId },
            subject: refNo,
            blockedReason: approveBlockedReason,
            warning: approveWarning,
            categoryCode,
          }}
          refNoOf={() => refNo}
          onActed={() => undefined}
          onClose={() => setDecision(null)}
        />
      ) : null}
    </>
  );
}
